(() => {
  if (customElements.get('vehicle-fitment-selector')) return;

  class VehicleFitmentSelector extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.formId = this.dataset.formId;
      this.productId = this.dataset.productId;
      this.remember = this.dataset.remember !== 'false';
      this.brandSelect = this.querySelector('[data-vehicle-brand]');
      this.modelSelect = this.querySelector('[data-vehicle-model]');
      this.yearSelect = this.querySelector('[data-vehicle-year]');
      this.steeringWheelSelect = this.querySelector('[data-steering-wheel]');
      this.otherBrand = this.querySelector('[data-vehicle-other-brand]');
      this.otherModel = this.querySelector('[data-vehicle-other-model]');
      this.brandProperty = this.querySelector('[data-vehicle-brand-property]');
      this.modelProperty = this.querySelector('[data-vehicle-model-property]');
      this.yearProperty = this.querySelector('[data-vehicle-year-property]');
      this.steeringWheelProperty = this.querySelector('[data-steering-wheel-property]');
      this.vehicleProperty = this.querySelector('[data-vehicle-combined-property]');
      this.status = this.querySelector('[data-vehicle-status]');

      this.rows = Array.isArray(window.vehicleData) ? window.vehicleData : [];
      this.storageKey = `gm_vehicle_${this.productId}`;

      this.populateBrands();
      this.bindEvents();
      this.restoreSelection();
      this.updateGate();
      this.startButtonObserver();
    }

    uniqueSorted(values) {
      return [...new Set(values)].sort((a, b) => a.localeCompare(b, 'en-GB'));
    }

    option(value, label = value) {
      const option = document.createElement('option');
      option.value = value;
      option.textContent = label;
      return option;
    }

    resetSelect(select, placeholder) {
      select.innerHTML = '';
      select.append(this.option('', placeholder));
      select.value = '';
    }

    populateBrands() {
      this.resetSelect(this.brandSelect, 'Select the make');
      const brands = this.uniqueSorted(this.rows.map(row => row.brand).filter(Boolean));
      brands.forEach(brand => this.brandSelect.append(this.option(brand)));
      this.brandSelect.append(this.option('__other__', 'Other make'));
      this.resetModel();
    }

    resetModel() {
      this.resetSelect(this.modelSelect, 'Select the model');
      this.modelSelect.disabled = true;
      this.resetYear();
    }

    resetYear() {
      this.resetSelect(this.yearSelect, 'Select the year');
      this.yearSelect.disabled = true;
    }

    populateModels() {
      const brand = this.brandSelect.value;
      this.resetSelect(this.modelSelect, 'Select the model');

      if (!brand) {
        this.modelSelect.disabled = true;
        this.resetYear();
        return;
      }

      if (brand === '__other__') {
        this.modelSelect.append(this.option('__other__', 'Other model'));
        this.modelSelect.value = '__other__';
        this.modelSelect.disabled = false;
        this.showOtherFields();
        this.populateYears(true);
        return;
      }

      const models = this.uniqueSorted(
        this.rows.filter(row => row.brand === brand).map(row => row.model).filter(Boolean)
      );
      models.forEach(model => this.modelSelect.append(this.option(model)));
      this.modelSelect.append(this.option('__other__', 'Other model'));
      this.modelSelect.disabled = false;
      this.resetYear();
      this.showOtherFields();
    }

    populateYears(forceAllYears = false) {
      const brand = this.brandSelect.value;
      const model = this.modelSelect.value;
      this.resetSelect(this.yearSelect, 'Select the year');

      if (!brand || !model) {
        this.yearSelect.disabled = true;
        return;
      }

      let years = [];
      if (forceAllYears || brand === '__other__' || model === '__other__') {
        const currentYear = new Date().getFullYear();
        for (let y = currentYear; y >= 1980; y--) years.push(y);
      } else {
        const matching = this.rows.filter(row => row.brand === brand && row.model === model);
        matching.forEach(row => {
          const from = Number(row.from || row.year || 1980);
          const to = Number(row.to || row.year || new Date().getFullYear());
          for (let y = to; y >= from; y--) years.push(y);
        });
        years = [...new Set(years)].sort((a, b) => b - a);
      }

      years.forEach(year => this.yearSelect.append(this.option(String(year))));
      this.yearSelect.disabled = years.length === 0;
    }

    bindEvents() {
      this.brandSelect.addEventListener('change', () => {
        this.populateModels();
        this.sync();
      });

      this.modelSelect.addEventListener('change', () => {
        this.populateYears();
        this.showOtherFields();
        this.sync();
      });

      this.yearSelect.addEventListener('change', () => this.sync());
      if (this.steeringWheelSelect) this.steeringWheelSelect.addEventListener('change', () => this.sync());
      this.otherBrand.addEventListener('input', () => this.sync());
      this.otherModel.addEventListener('input', () => this.sync());

      const form = document.getElementById(this.formId);
      if (form) {
        form.addEventListener('submit', (event) => {
          if (!this.isComplete()) {
            event.preventDefault();
            event.stopImmediatePropagation();
            this.classList.add('is-invalid');
            this.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.brandSelect.focus({ preventScroll: true });
          }
        }, true);
      }
    }

    showOtherFields() {
      const showBrand = this.brandSelect.value === '__other__';
      const showModel = this.modelSelect.value === '__other__';
      this.otherBrand.hidden = !showBrand;
      this.otherModel.hidden = !showModel;
      this.otherBrand.required = showBrand;
      this.otherModel.required = showModel;
    }

    actualBrand() {
      return this.brandSelect.value === '__other__' ? this.otherBrand.value.trim() : this.brandSelect.value;
    }

    actualModel() {
      return this.modelSelect.value === '__other__' ? this.otherModel.value.trim() : this.modelSelect.value;
    }

    isComplete() {
      return Boolean(this.actualBrand() && this.actualModel() && this.yearSelect.value && this.steeringWheelSelect && this.steeringWheelSelect.value);
    }

    sync() {
      const brand = this.actualBrand();
      const model = this.actualModel();
      const year = this.yearSelect.value;
      const steeringWheel = this.steeringWheelSelect ? this.steeringWheelSelect.value : '';

      this.brandProperty.value = brand;
      this.modelProperty.value = model;
      this.yearProperty.value = year;
      if (this.steeringWheelProperty) this.steeringWheelProperty.value = steeringWheel;
      this.vehicleProperty.value = [brand, model, year, steeringWheel].filter(Boolean).join(' • ');

      if (this.remember && this.isComplete()) {
        try {
          const payload = JSON.stringify({
            brandSelect: this.brandSelect.value,
            modelSelect: this.modelSelect.value,
            brand,
            model,
            year
          });
          localStorage.setItem(this.storageKey, payload);
          localStorage.setItem('gm_last_vehicle', payload);
        } catch (_) {}
      }

      this.classList.remove('is-invalid');
      this.updateGate();
    }

    restoreSelection() {
      if (!this.remember) return;
      let saved = null;
      try {
        saved = JSON.parse(localStorage.getItem(this.storageKey) || localStorage.getItem('gm_last_vehicle') || 'null');
      } catch (_) {}
      if (!saved) return;

      const brandExists = [...this.brandSelect.options].some(option => option.value === saved.brandSelect || option.value === saved.brand);
      if (brandExists) {
        this.brandSelect.value = saved.brandSelect || saved.brand;
      } else if (saved.brand) {
        this.brandSelect.value = '__other__';
        this.otherBrand.value = saved.brand;
      }

      this.populateModels();

      const modelExists = [...this.modelSelect.options].some(option => option.value === saved.modelSelect || option.value === saved.model);
      if (modelExists) {
        this.modelSelect.value = saved.modelSelect || saved.model;
      } else if (saved.model) {
        this.modelSelect.value = '__other__';
        this.otherModel.value = saved.model;
      }

      this.populateYears();
      this.showOtherFields();
      if ([...this.yearSelect.options].some(option => option.value === String(saved.year))) {
        this.yearSelect.value = String(saved.year);
      }
      this.sync();
    }

    matchingSubmitButtons() {
      const form = document.getElementById(this.formId);
      const inside = form ? [...form.querySelectorAll('button[type="submit"], input[type="submit"]')] : [];
      const outside = [...document.querySelectorAll(`[form="${this.formId}"][type="submit"], button[form="${this.formId}"]`)];
      return [...new Set([...inside, ...outside])];
    }

    setButtonsDisabled(disabled) {
      this.matchingSubmitButtons().forEach(button => {
        if (button.closest('vehicle-fitment-selector')) return;
        if (disabled) {
          if (!button.dataset.gmWasDisabled) button.dataset.gmWasDisabled = button.disabled ? 'true' : 'false';
          button.disabled = true;
          button.setAttribute('aria-disabled', 'true');
          button.classList.add('gm-vehicle-disabled');
        } else if (button.classList.contains('gm-vehicle-disabled') || button.dataset.gmWasDisabled) {
          button.disabled = button.dataset.gmWasDisabled === 'true';
          if (!button.disabled) button.removeAttribute('aria-disabled');
          button.classList.remove('gm-vehicle-disabled');
          delete button.dataset.gmWasDisabled;
        }
      });
    }

    updateGate() {
      const ready = this.isComplete();
      this.setButtonsDisabled(!ready);

      if (this.status) {
        this.status.classList.toggle('is-ready', ready);
        const stateKey = ready ? `ready:${this.vehicleProperty.value}` : 'waiting';
        if (this.status.dataset.state !== stateKey) {
          this.status.dataset.state = stateKey;
          this.status.innerHTML = ready
            ? `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="m5 10.4 3.1 3.1L15.3 6" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg><span>Vehicle selected: <strong>${this.escapeHtml(this.vehicleProperty.value)}</strong></span>`
            : `<svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="7.25" stroke="currentColor" stroke-width="1.5"/><path d="M10 6.8v3.8M10 13.5h.01" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg><span>Select the make, model and year to enable checkout.</span>`;
        }
      }
    }

    startButtonObserver() {
      this.observer = new MutationObserver(() => this.updateGate());
      const root = document.body;
      this.observer.observe(root, { childList: true, subtree: true });
      setTimeout(() => this.updateGate(), 250);
      setTimeout(() => this.updateGate(), 1000);
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }
  }

  customElements.define('vehicle-fitment-selector', VehicleFitmentSelector);
})();
